import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import Swal from "sweetalert2";
// Same stylesheet as Add New Product so the form matches the rest of the back office
import "./AdminAddProduct.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminHomepagePopup() {
  const { authFetch } = useContext(AuthContext);
  const navigate = useNavigate();

  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editId, setEditId] = useState(null);
  const [title, setTitle] = useState("");
  const [startAt, setStartAt] = useState("");
  const [endAt, setEndAt] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  // yyyy-MM-ddTHH:mm for <input type="datetime-local">
  const toInputValue = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d)) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const formatDisplay = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    if (isNaN(d)) return "-";
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const fetchPopups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/HomepagePopups`);
      if (!res.ok) throw new Error(`Failed to load popups (${res.status})`);
      setPopups(await res.json());
    } catch (err) {
      Swal.fire("Error", err.message || "Failed to load popups.", "error");
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchPopups();
  }, [fetchPopups]);

  const resetForm = () => {
    setEditId(null);
    setTitle("");
    setStartAt("");
    setEndAt("");
    setIsActive(true);
    setImageFile(null);
    setImagePreview(null);
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setTitle(p.title || "");
    setStartAt(toInputValue(p.startAt));
    setEndAt(toInputValue(p.endAt));
    setIsActive(p.isActive);
    setImageFile(null);
    setImagePreview(p.imageUrl || null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      Swal.fire("File Too Large", "Please select an image under 5MB.", "error");
      return;
    }
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["jpg", "jpeg", "png"].includes(ext)) {
      Swal.fire("Invalid File Type", "Please select a JPG, JPEG or PNG image.", "error");
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editId && !imageFile) {
      Swal.fire("Validation", "Please upload an image.", "warning");
      return;
    }
    if (startAt && endAt && new Date(endAt) < new Date(startAt)) {
      Swal.fire("Validation", "End date must be after the start date.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("Title", title.trim());
    if (startAt) formData.append("StartAt", startAt);
    if (endAt) formData.append("EndAt", endAt);
    formData.append("IsActive", isActive);
    if (imageFile) formData.append("ImageFile", imageFile);

    setSaving(true);
    try {
      const url = editId
        ? `${API_BASE_URL}/api/HomepagePopups/${editId}`
        : `${API_BASE_URL}/api/HomepagePopups`;

      const res = await authFetch(url, { method: editId ? "PUT" : "POST", body: formData });

      if (res.ok) {
        resetForm();
        fetchPopups();
        Swal.fire({
          icon: "success",
          title: editId ? "Updated" : "Added",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        const err = await res.json().catch(() => null);
        Swal.fire("Error", err?.message || `Failed to save (${res.status})`, "error");
      }
    } catch (err) {
      Swal.fire("Error", "Failed to save popup.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    const result = await Swal.fire({
      title: "Delete Pop-up?",
      text: p.title ? `Delete "${p.title}"?` : "Delete this pop-up?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;

    try {
      const res = await authFetch(`${API_BASE_URL}/api/HomepagePopups/${p.id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        if (editId === p.id) resetForm();
        fetchPopups();
        Swal.fire({ icon: "success", title: "Deleted", timer: 1500, showConfirmButton: false });
      } else {
        Swal.fire("Error", "Failed to delete popup.", "error");
      }
    } catch (err) {
      Swal.fire("Error", "Failed to delete popup.", "error");
    }
  };

  return (
    <div className="admin-add-product-container">
      <div className="admin-add-product-header">
        <button
          className="admin-add-product-back-btn"
          onClick={() => navigate("/admin/dashboard")}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      <div className="admin-add-product-title">
        <h1>Homepage Pop-up</h1>
      </div>

      <form onSubmit={handleSubmit} className="admin-add-product-details-form">
        <div className="admin-add-product-form-group">
          <label>Title (for your reference only):</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Raya Promotion 2026"
          />
        </div>

        <div className="admin-add-product-form-group">
          <label>Show From:</label>
          <input
            type="datetime-local"
            value={startAt}
            onChange={(e) => setStartAt(e.target.value)}
          />
          <small style={{ display: "block", marginTop: "4px", color: "#777", textAlign: "left" }}>
            Leave empty to start showing immediately.
          </small>
        </div>

        <div className="admin-add-product-form-group">
          <label>Show Until:</label>
          <input
            type="datetime-local"
            value={endAt}
            onChange={(e) => setEndAt(e.target.value)}
          />
          <small style={{ display: "block", marginTop: "4px", color: "#777", textAlign: "left" }}>
            Leave empty to keep showing until you switch it off.
          </small>
        </div>

        <div className="admin-add-product-form-group">
          <label>Status:</label>
          <select
            value={isActive ? "1" : "0"}
            onChange={(e) => setIsActive(e.target.value === "1")}
          >
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          <small style={{ display: "block", marginTop: "4px", color: "#777", textAlign: "left" }}>
            Inactive never shows, whatever the schedule says.
          </small>
        </div>

        <div className="admin-add-product-form-group">
          <label>Pop-up Image:</label>
          <div className="admin-add-product-images-row">
            <div className="admin-add-product-main-image-section">
              <div className="admin-add-product-main-image-label">Image</div>
              <div className="admin-add-product-image-item">
                <label htmlFor="popupImageUpload">
                  <div className="admin-add-product-image-card">
                    {imagePreview ? (
                      <img src={imagePreview} alt="Pop-up Preview" />
                    ) : (
                      <div className="admin-add-product-upload-placeholder">
                        <i className="fas fa-cloud-upload-alt"></i>
                        <div className="admin-add-product-upload-text">
                          <span>Image</span>
                          <span className="admin-add-product-upload-subtext">Max 5MB</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    id="popupImageUpload"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
            </div>
          </div>
          {editId && (
            <small style={{ display: "block", marginTop: "6px", color: "#777", textAlign: "left" }}>
              Leave the current image in place to keep it.
            </small>
          )}
        </div>

        <button
          type="submit"
          className="admin-add-product-btn-primary"
          disabled={saving}
        >
          <i className="fas fa-save"></i>{" "}
          {saving ? "Saving..." : editId ? "Save Changes" : "Add Pop-up"}
        </button>

        {editId && (
          <button
            type="button"
            onClick={resetForm}
            style={{
              marginTop: "10px",
              width: "100%",
              padding: "12px",
              background: "#f5f5f5",
              border: "1px solid #ddd",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "15px",
            }}
          >
            Cancel editing
          </button>
        )}
      </form>

      <div className="admin-add-product-details-form">
        <h2 style={{ textAlign: "left", fontSize: "18px", color: "#2a3f54", marginTop: 0 }}>
          Existing Pop-ups
        </h2>

        {loading ? (
          <p style={{ textAlign: "left", color: "#777" }}>Loading...</p>
        ) : popups.length === 0 ? (
          <p style={{ textAlign: "left", color: "#777" }}>
            No pop-up has been set up. The homepage will not show one.
          </p>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {popups.map((p) => (
              <div
                key={p.id}
                style={{
                  display: "flex",
                  gap: "14px",
                  alignItems: "center",
                  padding: "12px",
                  border: "1px solid #eee",
                  borderRadius: "8px",
                  textAlign: "left",
                }}
              >
                <img
                  src={p.imageUrl}
                  alt={p.title || "Pop-up"}
                  style={{
                    width: "110px",
                    height: "80px",
                    objectFit: "cover",
                    borderRadius: "6px",
                    border: "1px solid #eee",
                  }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, color: "#2a3f54" }}>
                    {p.title || <span style={{ color: "#999" }}>(no title)</span>}{" "}
                    <span className={`admin-list-status ${p.isLive ? "active" : "inactive"}`}>
                      {p.isLive ? "Showing now" : p.isActive ? "Scheduled / Expired" : "Inactive"}
                    </span>
                  </div>
                  <div style={{ fontSize: "13px", color: "#666", marginTop: "4px" }}>
                    From: {formatDisplay(p.startAt)} &nbsp;&middot;&nbsp; Until:{" "}
                    {formatDisplay(p.endAt)}
                  </div>
                </div>
                <button
                  type="button"
                  className="admin-add-product-btn-add-value"
                  onClick={() => startEdit(p)}
                >
                  <i className="far fa-edit"></i> Edit
                </button>
                <button
                  type="button"
                  className="admin-add-product-btn-remove-value"
                  onClick={() => handleDelete(p)}
                >
                  <i className="fas fa-trash"></i> Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminHomepagePopup;
